import React, { Suspense, lazy, useRef, useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { useContext } from 'react';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useHasRole } from '@/hooks/useHasRole';
import { LogOut, ChevronDown, FileText, Wallet, Settings, ChefHat, Receipt, LifeBuoy } from 'lucide-react';
import { getCreateElectronicDocumentFormToken, openCreateElectronicDocumentForm } from '@/services/hacienda';
import { ConfirmationAlertDialog } from '@/components/ConfirmationAlertDialog';
import Spinner from '@/components/ui/spinner';
import { toast } from '@/hooks/use-toast';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@/types/featureFlags';
import { navigateTo } from '@/utils/navigationUtils';
import { useLocation } from 'react-router';

// First open only. Keeps the AI SDK out of the app shell, and a local Suspense
// so the route-level fallback does not replace the POS while this chunk loads.
const HelpChat = lazy(() => import('@/components/help-chat/HelpChat'));

export default function ProfileMenu() {
  const { profile, user, signOut, simpleUser } = useAuth();
  const { currentOrganization } = useOrganization();
  const { translator } = useContext(LanguageProviderContext);
  const { isAdminOrManager } = useHasRole();
  const location = useLocation();
  const isKitchenView = location.pathname === '/kitchen';
  const [isCustomInvoiceWarningOpen, setIsCustomInvoiceWarningOpen] = useState(false);
  const [isCreatingCustomInvoice, setIsCreatingCustomInvoice] = useState(false);
  const [isHelpChatOpen, setIsHelpChatOpen] = useState(false);
  const [helpChatRequested, setHelpChatRequested] = useState(false);

  const displayName =
    simpleUser?.username ||
    profile?.full_name ||
    user?.email?.split('@')[0] ||
    'User';
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const openingHelpChatRef = useRef(false);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);

  const updateWidth = () => {
    const w = triggerRef.current?.offsetWidth;
    if (w) setMenuWidth(w);
  };

  useEffect(() => {
    // update on mount in case layout shifted
    updateWidth();
    const onResize = () => {
      updateWidth();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const digitalInvoicesEnabled = currentOrganization?.digitalInvoicesEnabled ?? false;
  const { isEnabled } = useFeatureFlag();
  const digitalInvoicesFeatureEnabled = isEnabled(FEATURE_FLAGS.DIGITAL_INVOICES);
  const kitchenViewEnabled = isEnabled(FEATURE_FLAGS.KITCHEN_VIEW);
  const chatSupportEnabled = isEnabled(FEATURE_FLAGS.CHAT_SUPPORT);
  const canCreateElectronicDocument = digitalInvoicesFeatureEnabled && digitalInvoicesEnabled && !!currentOrganization?.haciendaApiKey;

  const handleCreateCustomInvoice = async () => {
    if (!currentOrganization?.haciendaApiKey || isCreatingCustomInvoice) return;

    // Open tab synchronously (user gesture) — window.open after await is blocked or strips URL.
    let embedTab: Window | null = null;
    if (import.meta.env.VITE_FACTORA_URL?.trim()) {
      try {
        embedTab = window.open('about:blank', '_blank');
      } catch {
        embedTab = null;
      }
    }

    setIsCreatingCustomInvoice(true);
    try {
      const { token, refreshToken } = await getCreateElectronicDocumentFormToken();
      openCreateElectronicDocumentForm(token, refreshToken, embedTab);
      setIsCustomInvoiceWarningOpen(false);
    } catch (error) {
      embedTab?.close();
      console.error('Error opeining Factora:', error);
      toast({
        title: translator('Settings.createElectronicDocumentError'),
        description: translator('Settings.createElectronicDocumentErrorDescription') + error,
        variant: 'destructive',
      });
    } finally {
      setIsCreatingCustomInvoice(false);
    }
  };

  return (
    <DropdownMenu onOpenChange={(open: boolean) => { if (open) updateWidth(); }}>
      <DropdownMenuTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          className="flex items-center gap-3 w-10 p-0 rounded-full md:w-auto md:px-3 md:py-1"
          title={displayName}
        >
          {profile?.avatar_url ? (
            <img alt="Profile avatar" src={profile.avatar_url} className="h-8 w-8 rounded-full object-cover" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-sm font-semibold">
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
          <span className="hidden md:inline-block text-sm text-foreground truncate max-w-[120px]">
            {displayName}
          </span>
          <ChevronDown className="hidden md:block w-4 h-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        style={menuWidth ? { width: menuWidth } : undefined}
        onCloseAutoFocus={(event) => {
          if (!openingHelpChatRef.current) return;
          openingHelpChatRef.current = false;
          event.preventDefault();
        }}
      >
        <DropdownMenuItem onSelect={() => navigateTo('/subscription')}>
          <Wallet className="mr-2 h-4 w-4" /> {translator('Settings.subscriptionManagement')}
        </DropdownMenuItem>
        {canCreateElectronicDocument && (
          <DropdownMenuItem
            onSelect={() => setIsCustomInvoiceWarningOpen(true)}
            disabled={isCreatingCustomInvoice}
          >
            {isCreatingCustomInvoice ? (
              <Spinner className="mr-2 h-4 w-4" />
            ) : (
              <FileText className="mr-2 h-4 w-4" />
            )}
            {translator('Global.goToFacturador')}
          </DropdownMenuItem>
        )}
        {(kitchenViewEnabled || isKitchenView) && (
          <DropdownMenuItem
            onSelect={() => navigateTo(isKitchenView ? '/tabs' : '/kitchen')}
          >
            {isKitchenView ? (
              <>
                <Receipt className="mr-2 h-4 w-4" /> {translator('Kitchen.posView')}
              </>
            ) : (
              <>
                <ChefHat className="mr-2 h-4 w-4" /> {translator('Kitchen.title')}
              </>
            )}
          </DropdownMenuItem>
        )}
         <DropdownMenuItem onSelect={() => navigateTo('/settings')}>
          <Settings className="mr-2 h-4 w-4" /> {translator('Home.settings')}
        </DropdownMenuItem>
        {chatSupportEnabled && (
          <DropdownMenuItem
            onSelect={() => {
              openingHelpChatRef.current = true;
              setHelpChatRequested(true);
              setIsHelpChatOpen(true);
            }}
          >
            <LifeBuoy className="mr-2 h-4 w-4" /> {translator('HelpChat.menuItem')}
          </DropdownMenuItem>
        )}
        {(isAdminOrManager() || canCreateElectronicDocument) && <DropdownMenuSeparator />}
        <DropdownMenuItem
          onSelect={async () => {
            try {
              await signOut();
            } catch (e) {
              console.error(e);
            }
          }}
        >
          <LogOut className="mr-2 h-4 w-4" /> {translator('Global.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
      <ConfirmationAlertDialog
        open={isCustomInvoiceWarningOpen}
        onOpenChange={setIsCustomInvoiceWarningOpen}
        title={translator('Settings.goToCreateElectronicDocument')}
        description={translator('Settings.createElectronicDocumentWarningDescription')}
        confirmLabel={translator('Sales.continue')}
        cancelLabel={translator('Global.cancel')}
        onConfirm={handleCreateCustomInvoice}
        isConfirming={isCreatingCustomInvoice}
      />
      {chatSupportEnabled && helpChatRequested && (
        <Suspense fallback={null}>
          <HelpChat
            open={isHelpChatOpen}
            onOpenChange={setIsHelpChatOpen}
            returnFocusRef={triggerRef}
          />
        </Suspense>
      )}
    </DropdownMenu>
  );
}